import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getSoundboardTexture } from '../soundboard-texture.js';

// Orchestrator registry entry (see visualizer-registry.js). Wide bpm range
// and low movement/energyLevel on purpose - this is the one visualizer in
// the library explicitly built for UNMETERED acoustic material (see the
// header comment below), so it should score well whether or not a track has
// any usable tempo at all.
export const PROFILE = {
  id: 'resonance',
  family: 'organic',
  energyLevel: 0.25,
  bpmMin: 40,
  bpmMax: 160,
  movement: 0.25,
  danceability: 0.30,
  tonality: 0.85,
  prefers: { dynamics: [0.45, 1.0], percussiveness: [0.0, 0.50] },
  enabled: true
};

// Resonance: damped string oscillators, excited by envelope RISE and left to
// ring out. Built for acoustic material - guitar, piano, folk, classical,
// world music - where the visual event is a note's DECAY and the silence
// between notes, not a beat to strobe on.
//
// THE ONE RULE THIS FILE NEVER BREAKS: no director.pulse(), no beat pulse,
// no hard cut, no strobe. Every bit of reactivity here flows through
// director.bands (continuous, always present, never gated on a beat grid)
// so this reads exactly as well on a rubato piano solo with
// hasReliableGrid===false as it does on a mixed, gridded track. That is this
// visualizer's entire reason for existing - see PROFILE.prefers above,
// which biases the orchestrator toward exactly this repertoire.
//
// PHYSICS (CPU side, negligible cost, zero per-frame allocation): each
// string is three floats - amplitude, phase, its own fixed frequency.
//   freq_i    spread geometrically over [0.8, 9.0] (a visual rate, not audio)
//   mode_i    = 1 + floor(i/4) - antinode count of the standing wave
//   damping_i = 0.55 * (1 + freq_i * 0.22)
// Trebles ring out faster than basses, same as a real instrument - that
// asymmetry is what makes the decay read as physical rather than decorative.
// Only the RISE of a band's envelope excites a string (a sustained note does
// not keep pumping energy in); everything else is exp(-damping*dt) decay and
// phase advance. See _stepPhysics.
//
// RENDERING: one InstancedBufferGeometry per layer (main strings, sympathetic
// strings) - a 256-vertex ribbon strip (128 samples along the string x 2 edges),
// instanced once per string. Per-frame cost is exactly two flat-array uniform
// writes (uAmp/uPhase) - no attribute uploads, no per-instance CPU transform
// work. Screen-space pixel width (2px rest -> 5px at full amplitude), a
// near-white core past amplitude 0.6, everything else static per-instance
// attributes set once at construction.
export const TIER_LAYOUT = {
  0: { mainCount: 18, symCount: 0, background: true },
  1: { mainCount: 24, symCount: 12, background: true },
  2: { mainCount: 24, symCount: 16, background: true }
};
const DEFAULT_TIER = 1;

// 128, not the spec's 24: the highest strings vibrate in 6 half-waves, and at
// 24 samples a half-wave was two straight segments - the strings read as
// zigzags with corners instead of curves. 16+ samples per half-wave is round
// at any amplitude, and 256 vertices x 40 instances is still nothing.
const SAMPLES_PER_STRING = 128;
const STRING_CENTER_Y = 3.0; // matches the camera's own look-at Y
const STRING_HALF_HEIGHT = 9.0; // strings run well past the frame top/bottom
const STRING_X_SPREAD = 8.0; // grave (left, -SPREAD) to aigu (right, +SPREAD)
const MAIN_Z = 0;
const SYMPATHETIC_Z = -3.5;

const FREQ_MIN = 0.8;
const FREQ_MAX = 9.0;
const REFERENCE_COUNT = 24; // the count the spec's own band-affinity indices (0-7/8-15/16-23) and sigma=4 are written against
const AFFINITY_SIGMA = 4;
// Band-affinity centers as fractions of REFERENCE_COUNT - 1 (spec's 0-7,
// 8-15, 16-23 -> midpoints 3.5, 11.5, 19.5) - scaled to whatever N a tier
// actually has, so the low/mid/high spatial layout stays proportionally the
// same at every tier instead of just truncating the treble end away.
const BAND_CENTER_FRAC = { low: 3.5 / (REFERENCE_COUNT - 1), mid: 11.5 / (REFERENCE_COUNT - 1), high: 19.5 / (REFERENCE_COUNT - 1) };

const SYM_AMPLITUDE_SCALE = 0.45;
const SYM_DAMPING_SLOWDOWN = 0.55; // "amortissement plus lent"

const DYNAMICS_EXCITE_MIN = 0.6;
const DYNAMICS_EXCITE_MAX = 1.4;
const DAMPING_PROGRESS_MIN = 0.85; // mid-track: notes ring out longest
const DAMPING_PROGRESS_MAX = 1.15; // intro/outro: shorter decay

const NO_GRID_PLUCK_MIN_S = 0.4;
const NO_GRID_PLUCK_MAX_S = 1.2;
const NO_GRID_PLUCK_AMP_MIN = 0.5;
const NO_GRID_PLUCK_AMP_MAX = 1.1;

const WIDTH_REST_PX = 2.0;
const WIDTH_FULL_PX = 5.0;
const WIDTH_FULL_AMP = 1.0; // amplitude at which the ribbon reaches WIDTH_FULL_PX
const WHITE_CORE_THRESHOLD = 0.6; // "coeur presque blanc quand l'amplitude depasse 0.6"
const REST_VISIBILITY_AMP = 0.5; // amplitude at which a string reaches full brightness

const PALETTE_SLOTS = 6;
const FALLBACK_COLORS = [0x8899ff, 0x66ddaa, 0xffcc66, 0xff8866, 0xcc77ff, 0x66bbff];
const PALETTE_LERP_MS = 800;

const BG_Z = -6;
// The built texture's own stored sRGB bytes already average ~0.14 (see
// build-soundboard.mjs's uniformity check, which targets ~35-37/255) - this
// is a MULTIPLIER on top of that, not the target displayed brightness
// itself, tuned so the peak result lands close to the spec's 0.10 ceiling
// (0.14 * 0.75 =~ 0.105). Getting this backwards (treating 0.10 as the
// multiplier directly, i.e. 0.14 * 0.10 =~ 0.014) is what made the
// background render as flat black the first time - crushed further still
// once ACESFilmicToneMapping's toe curve got hold of a value that small.
const BG_EXPOSURE_PEAK = 0.75; // "elle doit se deviner, pas se voir"
const BG_BREATH_MIN_FRAC = 0.7; // breathes DOWN from the peak with global energy, never above it
const BG_PLANE_WIDTH = 56;
const BG_PLANE_HEIGHT = 30;
const BG_REPEAT_WORLD_SIZE = 8; // one texture tile per ~8 world units

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

// Position-based, N-agnostic generalization of the spec's fixed 24-string
// formulas - see BAND_CENTER_FRAC above for why. i is the string's own
// index, count is however many strings this tier/layer actually has - the
// full [0.8, 9.0] range is always reached by the highest-index string,
// whatever count a given tier ends up with, so a lower tier's own treble
// string is never quietly muted relative to gaussianAffinity's own
// count-relative "this is the treble end" placement below.
function stringFreq(i, count) {
  const t = count > 1 ? i / (count - 1) : 0;
  return FREQ_MIN * Math.pow(FREQ_MAX / FREQ_MIN, t);
}

function stringMode(i) {
  return 1 + Math.floor(i / 4);
}

function stringDamping(freq) {
  return 0.55 * (1 + freq * 0.22);
}

function gaussianAffinity(i, count, bandFrac) {
  const center = bandFrac * (count - 1);
  const sigma = AFFINITY_SIGMA * (count / REFERENCE_COUNT);
  const d = (i - center) / Math.max(0.001, sigma);
  return Math.exp(-0.5 * d * d);
}

// Local safe-zone chunk - identical distance-field math to
// shaders/safeZone.glsl.js's shared SAFE_ZONE_VERTEX_CHUNK, but with this
// visualizer's own 0.30 floor (the shared chunk bakes in 0.32) - "Les cordes
// passent DERRIERE la pochette... plancher 0.30".
const RESONANCE_SAFE_ZONE_MIN_ATTENUATION = 0.30;
const RESONANCE_SAFE_ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float safeZoneAttenuation(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${RESONANCE_SAFE_ZONE_MIN_ATTENUATION}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;

// count is baked into the array sizes at shader-compile time - a fresh
// material is built per tier (see TIER_LAYOUT/QUALITY_TIER_AWARE_MODES), so
// this never needs to be a fixed upper bound.
function stringVertexShader(count, withColorGradient) {
  return `
    precision highp float;

    attribute float iIndex;
    attribute float iPosX;
    attribute float iMode;
    ${withColorGradient ? 'attribute float iColorT;' : ''}

    uniform float uAmp[${count}];
    uniform float uPhase[${count}];
    uniform float uZOffset;
    uniform float uPixelScale;
    uniform float uWidthRestPx;
    uniform float uWidthFullPx;
    uniform float uWidthFullAmp;
    ${withColorGradient ? `uniform vec3 uPalette[${PALETTE_SLOTS}];` : 'uniform vec3 uSymColor;'}

    varying float vAmp;
    varying float vSide;
    varying vec3 vColor;
    varying float vSafeAtt;

    ${RESONANCE_SAFE_ZONE_CHUNK}

    void main() {
      int idx = int(iIndex);
      float amp = uAmp[idx];
      float phase = uPhase[idx];

      float side = position.x;
      float t = position.y;
      float wave = amp * sin(iMode * 3.14159265 * t) * sin(phase);

      vec3 centerPos = vec3(
        iPosX + wave,
        ${STRING_CENTER_Y.toFixed(2)} + (t * 2.0 - 1.0) * ${STRING_HALF_HEIGHT.toFixed(2)},
        uZOffset
      );

      vec4 mv = modelViewMatrix * vec4(centerPos, 1.0);
      float viewDepth = max(-mv.z, 0.001);
      float worldPerPx = viewDepth / uPixelScale;
      float widthPx = mix(uWidthRestPx, uWidthFullPx, clamp(amp / uWidthFullAmp, 0.0, 1.0));
      float halfWidth = widthPx * 0.5 * worldPerPx;

      // the width is laid across the CURVE, not across the screen: offset
      // horizontally, a steep stretch of string came out thin and faceted
      float slope = amp * sin(phase) * iMode * 3.14159265 * cos(iMode * 3.14159265 * t) / ${(2 * STRING_HALF_HEIGHT).toFixed(1)};
      vec2 nrm = normalize(vec2(1.0, -slope));
      vec3 worldPos = centerPos + vec3(nrm * (halfWidth * side), 0.0);

      vAmp = amp;
      vSide = side;
      ${withColorGradient ? `
      float scaled = iColorT * ${(PALETTE_SLOTS - 1).toFixed(1)};
      int i0 = int(floor(scaled));
      int i1 = i0 + 1;
      float f = fract(scaled);
      vec3 c0 = uPalette[0];
      vec3 c1 = uPalette[0];
      for (int k = 0; k < ${PALETTE_SLOTS}; k++) {
        if (k == i0) c0 = uPalette[k];
        if (k == i1) c1 = uPalette[k];
      }
      vColor = mix(c0, c1, f);
      ` : 'vColor = uSymColor;'}

      gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
      vSafeAtt = safeZoneAttenuation(gl_Position);
    }
  `;
}

const stringFragmentShader = `
  precision mediump float;

  varying float vAmp;
  varying float vSide;
  varying vec3 vColor;
  varying float vSafeAtt;

  void main() {
    float edge = 1.0 - abs(vSide);
    edge = edge * edge;
    float coreMix = clamp((vAmp - ${WHITE_CORE_THRESHOLD.toFixed(2)}) / 0.4, 0.0, 1.0);
    vec3 hot = mix(vColor, vec3(1.0), 0.92);
    vec3 col = mix(vColor, hot, coreMix);
    float restVisibility = clamp(vAmp / ${REST_VISIBILITY_AMP.toFixed(2)}, 0.12, 1.0);
    vec3 finalColor = col * edge * restVisibility * vSafeAtt;
    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

const bgVertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const bgFragmentShader = `
  precision mediump float;
  uniform sampler2D uMap;
  uniform float uExposure;
  varying vec2 vUv;
  void main() {
    vec3 tex = texture2D(uMap, vUv).rgb;
    gl_FragColor = vec4(tex * uExposure, 1.0);
  }
`;

export class ResonanceVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    this._soundboardTexture = options && options.soundboardTexture !== undefined
      ? options.soundboardTexture
      : getSoundboardTexture();
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(0, STRING_CENTER_Y, 9.0);
    camera.lookAt(0, STRING_CENTER_Y, 0);

    const layout = TIER_LAYOUT[this._tier];
    this._mainCount = layout.mainCount;
    this._symCount = layout.symCount;

    // Pre-allocated once, mutated in place every frame - no per-frame
    // allocation anywhere in update()/the physics step.
    this._mainAmp = new Float32Array(this._mainCount);
    this._mainPhase = new Float32Array(this._mainCount);
    this._mainFreq = new Float32Array(this._mainCount);
    this._mainDamping = new Float32Array(this._mainCount);
    this._mainAffinityLow = new Float32Array(this._mainCount);
    this._mainAffinityMid = new Float32Array(this._mainCount);
    this._mainAffinityHigh = new Float32Array(this._mainCount);
    for (let i = 0; i < this._mainCount; i++) {
      const freq = stringFreq(i, this._mainCount);
      this._mainFreq[i] = freq;
      this._mainDamping[i] = stringDamping(freq);
      this._mainAffinityLow[i] = gaussianAffinity(i, this._mainCount, BAND_CENTER_FRAC.low);
      this._mainAffinityMid[i] = gaussianAffinity(i, this._mainCount, BAND_CENTER_FRAC.mid);
      this._mainAffinityHigh[i] = gaussianAffinity(i, this._mainCount, BAND_CENTER_FRAC.high);
    }

    if (this._symCount > 0) {
      this._symAmp = new Float32Array(this._symCount);
      this._symPhase = new Float32Array(this._symCount);
      this._symFreq = new Float32Array(this._symCount);
      this._symDamping = new Float32Array(this._symCount);
      for (let i = 0; i < this._symCount; i++) {
        const freq = stringFreq(i, this._symCount);
        this._symFreq[i] = freq;
        this._symDamping[i] = stringDamping(freq) * SYM_DAMPING_SLOWDOWN;
      }
    }

    this._buildMainMesh();
    if (this._symCount > 0) this._buildSymMesh();
    if (layout.background) this._buildBackground();

    this._time = 0;
    this._prevBandLow = 0;
    this._prevBandMid = 0;
    this._prevBandHigh = 0;
    this._nextRandomPluckAt = 0;

    this._paletteFrom = FALLBACK_COLORS.map((c) => new THREE.Color(c));
    this._paletteTo = FALLBACK_COLORS.map((c) => new THREE.Color(c));
    this._symColorFrom = new THREE.Color(FALLBACK_COLORS[0]).multiplyScalar(0.6);
    this._symColorTo = this._symColorFrom.clone();
    this._paletteLerpStart = -Infinity;
    this._lastPaletteRef = undefined;
    this._applyPaletteUniformsImmediate();
  }

  _quadRibbonGeometry() {
    const positions = new Float32Array(SAMPLES_PER_STRING * 2 * 3);
    for (let s = 0; s < SAMPLES_PER_STRING; s++) {
      const t = SAMPLES_PER_STRING > 1 ? s / (SAMPLES_PER_STRING - 1) : 0;
      positions[(s * 2) * 3 + 0] = -1;
      positions[(s * 2) * 3 + 1] = t;
      positions[(s * 2) * 3 + 2] = 0;
      positions[(s * 2 + 1) * 3 + 0] = 1;
      positions[(s * 2 + 1) * 3 + 1] = t;
      positions[(s * 2 + 1) * 3 + 2] = 0;
    }
    const indices = [];
    for (let s = 0; s < SAMPLES_PER_STRING - 1; s++) {
      const a = s * 2, b = s * 2 + 1, c = s * 2 + 2, d = s * 2 + 3;
      indices.push(a, b, c, b, d, c);
    }
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geometry;
  }

  _buildMainMesh() {
    const geometry = this._quadRibbonGeometry();
    const indices = new Float32Array(this._mainCount);
    const posX = new Float32Array(this._mainCount);
    const modes = new Float32Array(this._mainCount);
    const colorT = new Float32Array(this._mainCount);
    for (let i = 0; i < this._mainCount; i++) {
      indices[i] = i;
      const frac = this._mainCount > 1 ? i / (this._mainCount - 1) : 0;
      posX[i] = -STRING_X_SPREAD + frac * 2 * STRING_X_SPREAD;
      modes[i] = stringMode(i);
      colorT[i] = frac;
    }
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(indices, 1));
    geometry.setAttribute('iPosX', new THREE.InstancedBufferAttribute(posX, 1));
    geometry.setAttribute('iMode', new THREE.InstancedBufferAttribute(modes, 1));
    geometry.setAttribute('iColorT', new THREE.InstancedBufferAttribute(colorT, 1));
    geometry.instanceCount = this._mainCount;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, STRING_CENTER_Y, MAIN_Z), STRING_HALF_HEIGHT + STRING_X_SPREAD);
    this._mainGeometry = geometry;

    this._mainUniforms = Object.assign({}, safeZoneUniforms, {
      uAmp: { value: this._mainAmp },
      uPhase: { value: this._mainPhase },
      uZOffset: { value: MAIN_Z },
      uPixelScale: { value: 1 },
      uWidthRestPx: { value: WIDTH_REST_PX },
      uWidthFullPx: { value: WIDTH_FULL_PX },
      uWidthFullAmp: { value: WIDTH_FULL_AMP },
      uPalette: { value: FALLBACK_COLORS.map((c) => new THREE.Color(c)) }
    });

    this._mainMaterial = new THREE.ShaderMaterial({
      uniforms: this._mainUniforms,
      vertexShader: stringVertexShader(this._mainCount, true),
      fragmentShader: stringFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._mainMesh = new THREE.Mesh(geometry, this._mainMaterial);
    this._scene.add(this._mainMesh);
  }

  _buildSymMesh() {
    const geometry = this._quadRibbonGeometry();
    const indices = new Float32Array(this._symCount);
    const posX = new Float32Array(this._symCount);
    const modes = new Float32Array(this._symCount);
    for (let i = 0; i < this._symCount; i++) {
      indices[i] = i;
      const frac = this._symCount > 1 ? i / (this._symCount - 1) : 0;
      posX[i] = -STRING_X_SPREAD + frac * 2 * STRING_X_SPREAD;
      modes[i] = stringMode(i);
    }
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(indices, 1));
    geometry.setAttribute('iPosX', new THREE.InstancedBufferAttribute(posX, 1));
    geometry.setAttribute('iMode', new THREE.InstancedBufferAttribute(modes, 1));
    geometry.instanceCount = this._symCount;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, STRING_CENTER_Y, SYMPATHETIC_Z), STRING_HALF_HEIGHT + STRING_X_SPREAD);
    this._symGeometry = geometry;

    this._symUniforms = Object.assign({}, safeZoneUniforms, {
      uAmp: { value: this._symAmp },
      uPhase: { value: this._symPhase },
      uZOffset: { value: SYMPATHETIC_Z },
      uPixelScale: { value: 1 },
      uWidthRestPx: { value: WIDTH_REST_PX },
      uWidthFullPx: { value: WIDTH_FULL_PX },
      uWidthFullAmp: { value: WIDTH_FULL_AMP },
      uSymColor: { value: new THREE.Color(FALLBACK_COLORS[0]).multiplyScalar(0.6) }
    });

    this._symMaterial = new THREE.ShaderMaterial({
      uniforms: this._symUniforms,
      vertexShader: stringVertexShader(this._symCount, false),
      fragmentShader: stringFragmentShader,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._symMesh = new THREE.Mesh(geometry, this._symMaterial);
    this._scene.add(this._symMesh);
  }

  _buildBackground() {
    if (!this._soundboardTexture) {
      // "sans la texture, le visualizer rend sur noir sans planter" - no
      // mesh at all, the scene's own clear colour already is black.
      this._bgMesh = null;
      return;
    }
    const texture = this._soundboardTexture;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(BG_PLANE_WIDTH / BG_REPEAT_WORLD_SIZE, BG_PLANE_HEIGHT / BG_REPEAT_WORLD_SIZE);

    const geometry = new THREE.PlaneGeometry(BG_PLANE_WIDTH, BG_PLANE_HEIGHT);
    this._bgUniforms = {
      uMap: { value: texture },
      uExposure: { value: BG_EXPOSURE_PEAK }
    };
    this._bgMaterial = new THREE.ShaderMaterial({
      uniforms: this._bgUniforms,
      vertexShader: bgVertexShader,
      fragmentShader: bgFragmentShader,
      transparent: false,
      depthWrite: false,
      depthTest: true,
      fog: false,
      // scene-manager.js runs ACESFilmicToneMapping - its toe curve crushes
      // a value this small (a deliberately subtle background, per spec) to
      // indistinguishable-from-black. This background is meant to
      // display its own controlled brightness, not compete inside the same
      // HDR curve as bloom-lit foreground geometry - see lyric-flash.js's
      // own toneMapped:false for the same reasoning.
      toneMapped: false
    });
    this._bgMesh = new THREE.Mesh(geometry, this._bgMaterial);
    this._bgMesh.position.set(0, STRING_CENTER_Y, BG_Z);
    this._scene.add(this._bgMesh);
  }

  // Vivid colours mapped by horizontal position - see PALETTE_SLOTS. Fewer
  // than PALETTE_SLOTS vivid colours: pad with the last one so the gradient
  // still spans smoothly rather than collapsing into black slots.
  _resolvePaletteColors(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const out = [];
    if (vivid.length === 0) {
      for (let i = 0; i < PALETTE_SLOTS; i++) out.push(new THREE.Color(FALLBACK_COLORS[i]));
    } else {
      for (let i = 0; i < PALETTE_SLOTS; i++) {
        const src = vivid[Math.min(i, vivid.length - 1)];
        out.push(new THREE.Color(src));
      }
    }
    const symSource = dominant.length > 0 ? dominant[0] : (vivid.length > 0 ? vivid[0] : FALLBACK_COLORS[0]);
    const symColor = new THREE.Color(symSource);
    const hsl = { h: 0, s: 0, l: 0 };
    symColor.getHSL(hsl);
    symColor.setHSL(hsl.h, hsl.s * 0.35, hsl.l * 0.7);
    return { main: out, sym: symColor };
  }

  _applyPaletteUniformsImmediate() {
    for (let i = 0; i < PALETTE_SLOTS; i++) this._mainUniforms.uPalette.value[i].copy(this._paletteTo[i]);
    // Guarded, like uPixelScale below: TIER_LAYOUT[0] has symCount 0, so
    // init() skips _buildSymMesh entirely and there are no sympathetic
    // uniforms to write. Unguarded this threw on every tier-0 build - which
    // is the Pi and the TV, exactly where it is least visible in testing.
    if (this._symUniforms) this._symUniforms.uSymColor.value.copy(this._symColorTo);
  }

  // AdaptivePartyVisualizer.onTrackChange (see adaptive-party.js) only sets
  // its own internal recheck flag - it does NOT forward to whichever
  // sub-visualizer is currently composed in. Polling profile.artworkPalette
  // here instead is the same pattern laser-cathedral.js's _applyPalette call
  // site already uses for exactly this reason.
  _maybeBeginPaletteTransition(profile) {
    if (profile.artworkPalette === this._lastPaletteRef) return;
    this._lastPaletteRef = profile.artworkPalette;
    const resolved = this._resolvePaletteColors(profile);
    // Snapshot wherever the lerp currently sits as the new FROM, so a
    // change mid-transition doesn't jump.
    const t = clamp01((performance.now() - this._paletteLerpStart) / PALETTE_LERP_MS);
    for (let i = 0; i < PALETTE_SLOTS; i++) {
      this._paletteFrom[i].lerp(this._paletteTo[i], t);
    }
    this._symColorFrom.lerp(this._symColorTo, t);
    this._paletteTo = resolved.main;
    this._symColorTo = resolved.sym;
    this._paletteLerpStart = performance.now();
  }

  _updatePaletteLerp() {
    const t = clamp01((performance.now() - this._paletteLerpStart) / PALETTE_LERP_MS);
    for (let i = 0; i < PALETTE_SLOTS; i++) {
      this._mainUniforms.uPalette.value[i].copy(this._paletteFrom[i]).lerp(this._paletteTo[i], t);
    }
    if (this._symUniforms) {
      this._symUniforms.uSymColor.value.copy(this._symColorFrom).lerp(this._symColorTo, t);
    }
  }

  // The core damped-oscillator step - see this file's header. Pure per-string
  // arithmetic, no allocation: rise*affinity*uExcite in, exp(-damping*dt*
  // globalDampingMult) decay, phase += freq*dt always.
  _stepMainStrings(riseLow, riseMid, riseHigh, uExcite, globalDampingMult, dt) {
    for (let i = 0; i < this._mainCount; i++) {
      const excitation = (riseLow * this._mainAffinityLow[i] + riseMid * this._mainAffinityMid[i] + riseHigh * this._mainAffinityHigh[i]) * uExcite;
      let amp = this._mainAmp[i] + excitation;
      amp *= Math.exp(-this._mainDamping[i] * globalDampingMult * dt);
      this._mainAmp[i] = amp;
      this._mainPhase[i] += this._mainFreq[i] * dt;
    }
  }

  _stepSymStrings(riseLow, uExcite, globalDampingMult, dt) {
    const gain = uExcite * SYM_AMPLITUDE_SCALE;
    for (let i = 0; i < this._symCount; i++) {
      let amp = this._symAmp[i] + riseLow * gain;
      amp *= Math.exp(-this._symDamping[i] * globalDampingMult * dt);
      this._symAmp[i] = amp;
      this._symPhase[i] += this._symFreq[i] * dt;
    }
  }

  // No usable analysis at all: pluck a random string every 400-1200ms
  // instead of trying to fall back to beatPhase - this repertoire has no
  // regular pulse to fall back TO, see this file's header.
  _stepNoGridFallback(globalDampingMult, dt) {
    if (this._time >= this._nextRandomPluckAt) {
      const idx = Math.floor(Math.random() * this._mainCount);
      this._mainAmp[idx] += NO_GRID_PLUCK_AMP_MIN + Math.random() * (NO_GRID_PLUCK_AMP_MAX - NO_GRID_PLUCK_AMP_MIN);
      this._nextRandomPluckAt = this._time + NO_GRID_PLUCK_MIN_S + Math.random() * (NO_GRID_PLUCK_MAX_S - NO_GRID_PLUCK_MIN_S);
    }
    for (let i = 0; i < this._mainCount; i++) {
      this._mainAmp[i] *= Math.exp(-this._mainDamping[i] * globalDampingMult * dt);
      this._mainPhase[i] += this._mainFreq[i] * dt;
    }
    for (let i = 0; i < this._symCount; i++) {
      this._symAmp[i] *= Math.exp(-this._symDamping[i] * globalDampingMult * dt);
      this._symPhase[i] += this._symFreq[i] * dt;
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._maybeBeginPaletteTransition(profile);
    this._updatePaletteLerp();

    if (!paused) {
      this._time += deltaTime;
      const director = profile.director;
      const hasGrid = !!(director && director.hasReliableGrid);
      const dynamics = director && director.character ? director.character.dynamics : null;
      const uExcite = dynamics != null ? lerp(DYNAMICS_EXCITE_MIN, DYNAMICS_EXCITE_MAX, dynamics) : 1.0;

      const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
      const globalDampingMult = DAMPING_PROGRESS_MAX - (DAMPING_PROGRESS_MAX - DAMPING_PROGRESS_MIN) * Math.sin(Math.PI * progress);

      if (hasGrid) {
        const bands = director.bands;
        const riseLow = Math.max(0, bands.low - this._prevBandLow);
        const riseMid = Math.max(0, bands.mid - this._prevBandMid);
        const riseHigh = Math.max(0, bands.high - this._prevBandHigh);
        this._stepMainStrings(riseLow, riseMid, riseHigh, uExcite, globalDampingMult, deltaTime);
        this._stepSymStrings(riseLow, uExcite, globalDampingMult, deltaTime);
        this._prevBandLow = bands.low;
        this._prevBandMid = bands.mid;
        this._prevBandHigh = bands.high;
      } else {
        this._stepNoGridFallback(globalDampingMult, deltaTime);
        this._prevBandLow = 0;
        this._prevBandMid = 0;
        this._prevBandHigh = 0;
      }
    }
    // paused: amplitude/phase arrays untouched this frame - "gel des
    // amplitudes ET des phases" falls out of simply not stepping them.

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    const pixelScale = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));
    this._mainUniforms.uPixelScale.value = pixelScale;
    if (this._symUniforms) this._symUniforms.uPixelScale.value = pixelScale;

    if (this._bgUniforms) {
      const intensity = profile.director ? clamp01(profile.director.intensity) : 0.5;
      this._bgUniforms.uExposure.value = BG_EXPOSURE_PEAK * lerp(BG_BREATH_MIN_FRAC, 1.0, intensity);
    }
  }

  dispose() {
    this._scene.remove(this._mainMesh);
    this._mainGeometry.dispose();
    this._mainMaterial.dispose();
    if (this._symMesh) {
      this._scene.remove(this._symMesh);
      this._symGeometry.dispose();
      this._symMaterial.dispose();
    }
    if (this._bgMesh) {
      this._scene.remove(this._bgMesh);
      this._bgMesh.geometry.dispose();
      this._bgMaterial.dispose();
      // uMap (the soundboard texture) is a shared, preloaded asset - see
      // soundboard-texture.js - and must NOT be disposed here.
    }
  }
}
